/**
 * @fileoverview Workspace "My Tasks" inbox (MOR-696 Stage 2)
 *
 * URL: /dashboard/my-tasks
 *
 * Cross-job inbox of every task/milestone in the active organisation assigned
 * to the current user. Streams `keyDates.queries.listForOrgPaginated` through
 * `useAllOrgKeyDates` (same feed as the workspace calendar; MOR-2473) and
 * renders the shared `TaskSurface`, pre-filtered to the signed-in user as
 * assignee and grouped by job. The assignee filter is a normal filter pill,
 * so the user can widen the view or layer on saved views.
 *
 * The assignee and job-status pills are mirrored into the query args: derived
 * statutory dates never carry an assignee, so the default view fetches only
 * the viewer's own persisted tasks. Removing a pill re-queries without it.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { ACTIVE_JOB_STATUSES } from "@cvx/jobs/schema";

import { useActiveOrg } from "@/utils/org-context";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import {
  TaskSurface,
  emptyFilters,
  useAllOrgKeyDates,
  type BoardMember,
  type TaskFilterState,
} from "@/calendar";
import { Spinner } from "@/ui/primitives/spinner";
import { TasksLoadError } from "./-ui.tasks-load-error";

export const Route = createFileRoute("/_app/_auth/dashboard/_layout/my-tasks")({
  component: MyTasksPage,
  errorComponent: TasksLoadError,
});

function MyTasksPage() {
  const { activeOrg } = useActiveOrg();
  const orgId = activeOrg?._id;

  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );

  const { data: jobs } = useQuery(
    convexQueryOrIdle(api.jobs.queries.listJobs, orgId ? { orgId } : undefined),
  );
  const { data: statuses } = useQuery(
    convexQueryOrIdle(
      api.tasks.statuses.listByOrg,
      orgId ? { orgId } : undefined,
    ),
  );
  const { data: labels } = useQuery(
    convexQueryOrIdle(
      api.tasks.labels.listByOrg,
      orgId ? { orgId } : undefined,
    ),
  );
  const { data: orgMembers } = useQuery(
    convexQueryOrIdle(
      api.organizations.queries.getOrganizationMembers,
      orgId ? { orgId } : undefined,
    ),
  );

  const members = useMemo<BoardMember[]>(
    () =>
      (orgMembers ?? []).map((m) => ({
        _id: String(m.user._id),
        firstName: m.user.firstName,
        lastName: m.user.lastName,
        email: m.user.email,
        avatarUrl: m.user.avatarUrl,
      })),
    [orgMembers],
  );

  const jobOptions = useMemo(
    () => (jobs ?? []).map((j) => ({ id: j._id, name: j.entityName })),
    [jobs],
  );

  // Pre-filter to the signed-in user and to in-flight jobs (pre-appointment /
  // day one / active); both surface as removable filter pills so the user can
  // widen the view or bake a different default into a saved view.
  const defaultFilters = useMemo<TaskFilterState>(
    () => ({
      ...emptyFilters(),
      ...(currentUser ? { assigneeUserIds: [String(currentUser._id)] } : {}),
      jobStatuses: [...ACTIVE_JOB_STATUSES],
    }),
    [currentUser],
  );

  // The live filter state, reported by TaskSurface. Null until it mounts, in
  // which case the defaults above are what it will start from.
  const [filters, setFilters] = useState<TaskFilterState | null>(null);
  const active = filters ?? defaultFilters;
  // Only a single-assignee pill maps onto the query arg; anything wider is
  // filtered client-side over the full stream as before.
  const assigneeUserId =
    active.assigneeUserIds.length === 1
      ? (active.assigneeUserIds[0] as Id<"users">)
      : undefined;

  const { events, isLoading, isLoadingAll, loadedCount } = useAllOrgKeyDates({
    // Hold the stream until the user is known so the first page already
    // carries the assignee pill instead of fetching everything and restarting.
    orgId: currentUser ? orgId : undefined,
    jobStatuses: active.jobStatuses,
    assigneeUserId,
  });

  if (!activeOrg) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        Select an organisation to view your tasks.
      </div>
    );
  }

  // Full-page spinner only before the surface has ever mounted. A later
  // pill change restarts the stream (new query args), and unmounting
  // TaskSurface then would throw away the filters the user just set; the
  // inline indicator below covers those reloads instead.
  if (
    (isLoading && filters === null) ||
    statuses === undefined ||
    currentUser === undefined ||
    !orgId
  ) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">My Tasks</h1>
          <p className="text-sm text-muted-foreground">
            All tasks assigned to you across your jobs. Remove the assignee
            filter to see all tasks assigned to your team.
          </p>
        </div>
        {isLoadingAll && (
          <span
            role="status"
            className="flex items-center gap-2 text-xs text-muted-foreground"
          >
            <Spinner className="h-3.5 w-3.5" />
            Loading more tasks ({loadedCount.toLocaleString()} so far)
          </span>
        )}
      </div>

      <TaskSurface
        scope="org"
        orgId={orgId}
        events={events}
        statuses={statuses}
        labels={labels ?? []}
        members={members}
        jobOptions={jobOptions}
        defaultFilters={defaultFilters}
        onFiltersChange={setFilters}
        defaultView="list"
        defaultGroupBy="job"
      />
    </div>
  );
}
